import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, catchError, finalize, forkJoin, of, takeUntil, timeout } from 'rxjs';
import { FlightBookingIssueControllerService, FlightHelpdeskControllerService } from '@booking/angular-booking-admin-service'
import { RESOURCE, TIME_OUT_REQUEST } from '@shared/constant';
import { ToastMessageService } from 'src/app/services/toast-message/toast-message.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { convertToUserTimeZone } from '@shared/utils/helper';
import { environment } from '@env/environment';
import { CurrencyConversionPipe } from '@soctrip-common/app-security';
import { UserService } from 'src/app/core/services/user/user.service';
import { CurrencyService } from 'src/app/services/currency.service';
import { CountryCodeService } from 'src/app/services/country-code.service';

@Component({
    selector: 'app-view-details',
    templateUrl: './view-details.component.html',
    styleUrls: ['./view-details.component.scss'],
    providers: [CurrencyConversionPipe],
})
export class ViewDetailsComponent {
    bookingId: string;
    isLoading: boolean = true;
    flightDetail: any;
    currentModeView = 'DEPART';
    currentSegments: any;
    currentSegmentType: string;
    currentFareRule: any;
    fareRuleVi: string;
    resource: string;
    isShowChangeRequestHistoryPopup: boolean = false;
    changeRequestHistory: any;
    titleMap: any;
    pathChangeRequest: string = 'admin/flight-admin/booking-helpdesk/change-request';
    pageNum: number;
    pageSize: number;
    isShowNoteUpdates: boolean = false;
    noteUpdate: string = '';
    isShowDiscardPopup: boolean = false;
    notes: any[];
    clickedConfirmNoteUpdates: boolean = false;
    isUpdateNote: boolean = false;
    baseUrl = environment.BASE_PATH;
    onDestroy$: Subject<void> = new Subject();
    userLanguage: string = ''
    environment = environment;
    userRole: any = ''
    accessToken = localStorage.getItem('access_token');
    private userProfileSubscription: Subscription = new Subscription();
    currency: any;
    selectedPassenger: any[];
    reasonCancel: any;
    currentTag: number = 0;
    type: string = '';
    cancelReasonValues = [
        'CHANGE_TRAVEL_PLANS',
        'DISSATISFACTION_BOOKING',
        'FOUND_BETTER_DEAL',
        'HAVE_ANOTHER_BOOKING',
        'PERSONAL_REASONS',
        'TRIP_CALLED_OFF',
        'OTHER_REASON'
    ];
    partner: string = '';

    constructor(
        private route: ActivatedRoute,
        private flightHelpdeskControllerService: FlightHelpdeskControllerService,
        private toastMessageService: ToastMessageService,
        private translateService: TranslateService,
        private userService: UserService,
        public currencyConversionPipe: CurrencyConversionPipe,
        private currencyService: CurrencyService,
        private countryCodeService: CountryCodeService,
        private flightBookingIssueControllerService: FlightBookingIssueControllerService
    ) { }

    ngOnInit() {
        this.initData();
        this.bookingId = this.route.snapshot.paramMap.get('bookingId')!;
        this.handleGetOrderDetail();
        this.userLanguage = this.translateService.currentLang || 'en';
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLanguage = event.lang;
        });
        this.userProfileSubscription = this.userService.getUserProfile().subscribe((data: any) => {
            this.userRole = data?.roles.join(',');
            this.currency = JSON.parse(localStorage.getItem('currency-conversation-data') || '');
            this.currencyService.receiveSignalChangeCurrency().subscribe(() => {
                // this.currencyService.updateCurrency();
                this.currency = JSON.parse(localStorage.getItem('currency-conversation-data') || '');
            });
        })
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.userProfileSubscription.unsubscribe()
    }

    initData() {
        const pageNum = this.route.snapshot.queryParamMap.get('page-num');
        const pageSize = this.route.snapshot.queryParamMap.get('page-size');
        this.pageNum = pageNum ? +pageNum : 1;
        this.pageSize = pageSize ? +pageSize : 10;

        this.titleMap = {
            ADDITIONAL_SERVICES_PURCHASING: this.translateService.instant('MY_FLIGHT_BOOKING_DETAIL.CHANGE_REQUEST.SERVICES_PURCHASE'),
            CANCEL_BOOKING: this.translateService.instant('MY_FLIGHT_BOOKING_DETAIL.CHANGE_REQUEST.CANCEL_BOOKING'),
            CHANGE_PASSENGER_NAME: this.translateService.instant('MY_FLIGHT_BOOKING_DETAIL.CHANGE_REQUEST.CHANGE_PASSENGER_NAME'),
            RESCHEDULE_FLIGHT: this.translateService.instant('MY_FLIGHT_BOOKING_DETAIL.CHANGE_REQUEST.RESCHEDULE_FLIGHT'),
        };

        this.changeRequestHistory = [
            {
                title: "RESCHEDULE_FLIGHT",
                detail: "test 123",
                requested_at: "2024-06-21T10:59:47.569049897",
                status: "Solved",
                id: "123",
            },
            {
                title: "RESCHEDULE_FLIGHT",
                detail: "test 456",
                requested_at: "2024-06-21T11:00:05.906078408",
                status: "Open",
                id: "456",
            },
            {
                title: "RESCHEDULE_FLIGHT",
                detail: "ádđssss",
                requested_at: "2024-06-21T11:00:53.668189571",
                status: "Open",
                id: "789",
            },
            {
                title: "RESCHEDULE_FLIGHT",
                detail: "Test description",
                requested_at: "2024-06-21T11:05:53.668189571",
                status: "Open",
                id: "012",
            }
        ];
    }

    handleGetOrderDetail() {
        if (!this.bookingId) return;

        const parts = this.bookingId.split('-');
        const mainOrderId = `${parts[0]}-${parts[1]}`;
        const subOrderId = parts.length === 2 ? mainOrderId : this.bookingId;

        forkJoin({
            booking: this.getBookingDetail(mainOrderId),
            subOrder: this.getSubOrderDetail(subOrderId)
        }).subscribe(({ booking, subOrder }) => {
            this.isLoading = false;

            if (booking?.success) {
                this.flightDetail = booking.data;
                const index = this.currentModeView === 'DEPART' ? 0 : 1;
                const itinerary = this.flightDetail.flight_info?.itineraries[index];
                if (itinerary) {
                    this.currentSegments = itinerary.segments;
                    this.currentSegmentType = itinerary.segment_type;
                    this.currentFareRule = itinerary.fare_rule;
                    this.fareRuleVi = itinerary.fare_rule_vi;
                    this.resource = itinerary.resource;
                }
                this.bookingId = this.flightDetail?.booking_info.reservation_code;
                this.notes = this.flightDetail?.notes;
            }

            if (subOrder?.success) {
                if (subOrder.data.cancel_history?.length) {
                    this.type = 'CANCEL';
                    const detailsData =
                        subOrder.data.cancel_history.find((item: any) => item.view_detail === subOrderId) ??
                        subOrder.data.cancel_history?.[0];

                    this.selectedPassenger = detailsData.canceled_list.map((item: any) => {
                        const itineraryIndex = item[0].itinerary_index;
                        const itinerary = this.flightDetail?.booking_info?.itineraries?.[itineraryIndex];
                        const segments = itinerary?.segments || [];
                        const firstSegment = segments[0];
                        const lastSegment = segments[segments.length - 1];

                        return {
                            departure_name: firstSegment?.departure?.city_name,
                            departure_code: firstSegment?.departure?.iata_code,
                            arrival_name: lastSegment?.arrival?.city_name,
                            arrival_code: lastSegment?.arrival?.iata_code,
                            passengers: item
                        };
                    });
                    this.reasonCancel = detailsData;
                }
                else if (subOrder.data.finding_flight_history?.length) {
                    this.type = 'REQUEST_REFUND';
                    const detailsData =
                        subOrder.data.finding_flight_history.find((item: any) => item.view_detail === subOrderId) ??
                        subOrder.data.finding_flight_history?.[0];

                    const source =
                        detailsData.action !== 'REQUEST_REFUND'
                            ? detailsData.flight_info
                            : detailsData.itineraries_for_refund;
                    const itineraries = (source || []).filter((i: any) => i);

                    this.selectedPassenger = itineraries.map((item: any) => {
                        const segments = item?.segments || [];
                        const firstSegment = segments[0];
                        const lastSegment = segments[segments.length - 1];

                        return {
                            departure_name: firstSegment?.departure?.city_name,
                            departure_code: firstSegment?.departure?.iata_code,
                            arrival_name: lastSegment?.arrival?.city_name,
                            arrival_code: lastSegment?.arrival?.iata_code,
                            passengers: item.passengers
                        };
                    });

                    const requestDetail = this.flightDetail.request_list.find(
                        (item: any) => item.request_type === 'FINDING_FLIGHT'
                    );
                    this.reasonCancel = requestDetail?.request_process?.[0];
                    this.loadPartner(requestDetail);
                }
            }
        });
    }


    public getBookingDetail(mainOrderId: string) {
        return this.flightHelpdeskControllerService.searchListBookingDetails(mainOrderId).pipe(
            timeout(TIME_OUT_REQUEST),
            catchError((error) => {
                if (error.name === 'TimeoutError') {
                    this.toastMessageService.showError('TIME_OUT_REQUEST');
                } else if (error.status === 0) {
                    this.toastMessageService.showError('ERR_INTERNET_DISCONNECTED');
                } else {
                    this.toastMessageService.showError(error.error.error);
                }
                return of(null);
            })
        );
    }

    public getSubOrderDetail(subOrderId: string) {
        return this.flightBookingIssueControllerService.viewChildReservation(subOrderId).pipe(
            catchError(() => of(null))
        );
    }

    changeViewMode(mode: string) {
        this.currentModeView = mode;
        // Re init data when mode changes
        const index = this.currentModeView === 'DEPART' ? 0 : 1;
        const itinerary = this.flightDetail.flight_info?.itineraries[index];
        if (itinerary) {
            this.currentSegments = itinerary.segments;
            this.currentSegmentType = itinerary.segment_type;
            this.currentFareRule = itinerary.fare_rule;
            this.fareRuleVi = itinerary.fare_rule_vi;
            this.resource = itinerary.resource
        }
    }

    onClickViewHistory() {
        this.isShowChangeRequestHistoryPopup = true;
    }

    closeChangeRequestHistoryPopup() {
        this.isShowChangeRequestHistoryPopup = false;
    }

    formatDateTime(dateTime: string): string {
        return moment(dateTime).format('HH:mm, MMM D, YYYY');
    }

    changeRequestStatusColor(status: string) {
        switch (status.toUpperCase()) {
            case 'SOLVED':
                return 'text-palette-emerald-600 bg-palette-emerald-50';
            case 'OPEN':
                return 'text-palette-gray-700 bg-palette-gray-100';
            default:
                return '';
        }
    }

    convertUserTimeZone(utcTime: string): string {
        return convertToUserTimeZone(utcTime)
    }

    getUTCUser(): string {
        return this.countryCodeService.getUTCOffset();
    }

    openNoteUpdates() {
        this.isShowNoteUpdates = true;
    }
    closeNoteUpdates() {
        if (this.noteUpdate) {
            this.isShowDiscardPopup = true;
        } else {
            this.isShowNoteUpdates = false;
            this.clickedConfirmNoteUpdates = false;
        }
    }

    onConfirmNoteUpdates() {
        if (this.noteUpdate.length && this.noteUpdate.length <= 254) {
            this.isUpdateNote = true;
            const body = {
                note: this.noteUpdate
            }
            this.flightHelpdeskControllerService.addFlightNote(body, this.bookingId)
                .pipe(
                    timeout(TIME_OUT_REQUEST),
                    takeUntil(this.onDestroy$),
                    catchError((error) => {
                        if (error.name === 'TimeoutError') {
                            this.toastMessageService.showError('TIME_OUT_REQUEST');
                        } else if (error.status === 0) {
                            this.toastMessageService.showError('ERR_INTERNET_DISCONNECTED');
                        } else {
                            this.toastMessageService.showError(error.error.error);
                        }
                        return of(null);
                    }),
                    finalize(() => {
                        this.isUpdateNote = false;
                    })
                )
                .subscribe((response: any) => {
                    if (response.success) {
                        this.notes = response?.data?.notes;
                        this.noteUpdate = '';
                        this.clickedConfirmNoteUpdates = false;
                        this.isShowNoteUpdates = false;
                        this.toastMessageService.showSuccess(this.translateService.instant('MY_FLIGHT_BOOKING_DETAIL.NOTE_UPDATE_SUCCESS'));
                    } else {
                        this.toastMessageService.showError(response.error.message);
                    }
                });
        } else {
            this.clickedConfirmNoteUpdates = true;
        }
    }

    onKeepEditing() {
        this.isShowDiscardPopup = false;
    }
    onDiscard() {
        this.isShowDiscardPopup = false;
        this.isShowNoteUpdates = false;
        this.clickedConfirmNoteUpdates = false;
        this.noteUpdate = '';
    }

    replaceWithDefaultAvatar(event: any): void {
        event.target.src = 'assets/images/default_user_avatar.png';
    }

    transformCurrency(value: number): any {
        let data = this.flightDetail;
        if (this.isSameCurrency() && this.flightDetail?.booking_info?.booking_status?.toUpperCase() !== 'PENDING PAYMENT') {
            return value * data?.exchange_rate?.exchange_rate_value
        } else {
            return value * data?.user_exchange_rate?.exchange_rate_value
        }
    }
    isSameCurrency() {
        if (this.currency?.currency?.toUpperCase() === this.flightDetail?.exchange_rate?.currency_exchange_rate?.toUpperCase()) {
            // if(this.flightDetail?.user_exchange_rate?.currency_exchange_rate){
            //     if(this.flightDetail?.user_exchange_rate?.currency_exchange_rate?.toUpperCase()==this.flightDetail?.exchange_rate?.currency_exchange_rate?.toUpperCase())
            //     {
            //         return true 
            //     }else{
            //         return false
            //     }
            // }
            // else{
            return true
            // }
        } else {
            return false
        }
    }

    isSameCurrencyPayment(){
      if(this.currency?.currency?.toUpperCase()===this.flightDetail?.exchange_rate?.currency_exchange_rate?.toUpperCase()){
          if(this.flightDetail?.user_exchange_rate?.currency_exchange_rate){
              if(this.flightDetail?.user_exchange_rate?.currency_exchange_rate?.toUpperCase()==this.flightDetail?.exchange_rate?.currency_exchange_rate?.toUpperCase())
              {
                  return true 
              }else{
                  return false
              }
          }
          else{
              return true
          }
      }else{
          return false
      }
    }

    transformCurrencyPayment(value: number, showSymbol: boolean = true): any {
    let data = this.flightDetail.exchange_rate;
    if (showSymbol) {
      return (value * data.exchange_rate_value).toLocaleString('en-US', {
        style: 'currency',
        currency: data.currency_exchange_rate,
      });
    } else {
      return (value * data.exchange_rate_value).toLocaleString('en-US', {
        style: 'decimal',
        maximumFractionDigits: 2,
      });
    }
  }

    isPredefinedReason(note: string | null | undefined): boolean {
        return !!note && this.cancelReasonValues.includes(note);
    }

    loadPartner(requestDetail: any) {
        const indexFail = requestDetail?.request_process?.[0]?.failed_itinerary_indices?.[0] || 0;

        if (indexFail == 0) {
            this.partner = RESOURCE[this.flightDetail?.booking_info?.resource] || this.flightDetail?.booking_info.resource;
        } else {
            this.partner = RESOURCE[this.flightDetail?.booking_info?.inbound_resource] || this.flightDetail?.booking_info.inbound_resource;
        }
    }
}
